import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { KiteProgressIndicator } from '@kite/react-kite';
import { UserCapabilityName } from 'types/userCapabilityName';
import { IAuthUserContext } from './types/authUserContext';
import { useAuthSession } from '../hooks/useAuthSession';
import authUserContextDefault from './utils/authUserDefault';
import { hasCapability } from './utils/authUserUtils';
import { useAuthUserFetcher } from './hooks/useAuthUserFetcher';

export const AuthUserContext = createContext<IAuthUserContext>(
  authUserContextDefault
);

/**
 * Auth user orchestrates the authenticated user once the authentication process is completed.
 * @see https://chalk.charter.com/display/ERSPM/FE+Auth+State+Pattern
 */
export const AuthUserContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);

  const { isAuthenticated, isAuthenticating, signOut } = useAuthSession();

  const { loadAuthUser, clearAuthUser, authUser } = useAuthUserFetcher({
    isAuthenticated,
    loadUserOnSuccess: useCallback(() => {
      setIsLoading(false);
    }, []),
    loadUserOnFailure: useCallback(() => {
      clearAuthUser();
      setIsError(true);
    }, []),
  });

  /**
   * Check whether the auth user has a capability
   * @param capabilities Single or array of capabilities to check
   */
  const userHasCapability = useCallback(
    (capabilities: UserCapabilityName | UserCapabilityName[]) =>
      hasCapability(capabilities, authUser?.capabilities || []),
    [authUser]
  );

  useEffect(() => {
    // Wait for Auth Session to finish loading as authUSer depends on a valid Auth Session
    if (isAuthenticating) {
      setIsError(false);
      return;
    }

    if (isAuthenticated && !isError) {
      // Load the auth user object when we have everything
      loadAuthUser();
      return;
    }

    // Handle Auth errors on Browser Refresh
    if (isLoading && isAuthenticated) {
      if (isError) {
        signOut({ showError: true }).then(() => {
          // do nothing
        }, console.error);
      }

      return;
    }

    // No auth user to load, make sure everything is cleared out.
    clearAuthUser();
    setIsLoading(false);
  }, [
    clearAuthUser,
    isAuthenticated,
    isAuthenticating,
    isError,
    isLoading,
    loadAuthUser,
    authUser,
    signOut,
  ]);

  return (
    <AuthUserContext.Provider
      value={useMemo(
        () => ({
          authUser,
          isError,
          isAuthenticated,
          hasCapability: userHasCapability,
        }),
        [authUser, isError, isAuthenticated, userHasCapability]
      )}
    >
      {!isLoading && children}
      {isLoading && (
        <div className="display-loader">
          <KiteProgressIndicator id="auth-user-loader" />
        </div>
      )}
    </AuthUserContext.Provider>
  );
};
